import {
  Coffee,
  Building2,
  Footprints,
  Waves,
  Sparkles,
  Hotel,
  Utensils,
  Ship,
  Trophy,
  Plane,
  Beer,
  Landmark,
  Medal,
  Users,
  ShoppingBag,
  TrainFront,
} from 'lucide-react';
import type { Day, NoteItem } from '../types';

// Coded from context-chicago/claude-analysis.md. Venue hours and closures checked in
// context-chicago/itinerary-verification.md (2 Oct 2026). Flights: QF93 + AA2998 land at O'Hare on the
// afternoon of Wed 7 Oct; AA607 departs 06:00 Tue 13 Oct. Base: a three-bedroom loft at 6156 S St Lawrence
// Ave, Woodlawn — the Loop is ~25 min on the Green Line, so downtown sightseeing is grouped into single trips.
export const chicagoDays: Day[] = [
  {
    date: 'Wednesday, October 7',
    dayNumber: 1,
    title: 'Arrival & Check-in',
    subtitle: 'QF93 → AA2998 · land at O’Hare in the afternoon · T-4',
    theme: 'chicago-arrival',
    activities: [
      {
        icon: <Plane size={24} />,
        title: "Land at O'Hare",
        description:
          'Melbourne → LA on QF93, then AA2998 to O’Hare, crossing the date line so it’s still Wednesday 7. Declare the gels, PF Carb Mix, Nomio and beetroot shots at US customs in LA — packaged supplements are fine once declared. Take a car to Woodlawn: about 25 miles, 45–75 minutes depending on traffic. The CTA route (Blue Line, change to the Green Line at Clark/Lake) takes well over an hour with bags.',
        location: "O'Hare International Airport",
        time: 'Afternoon',
        image: '/images/chicago/ohare.jpg',
      },
      {
        icon: <Hotel size={24} />,
        title: 'The loft — Woodlawn',
        description:
          'Three-bedroom, 2.5-bath loft at 6156 S St Lawrence Ave, on the South Side between Washington Park and the University of Chicago (rated 9.6 on Expedia/Vrbo). Check-in from 4 pm. It has a full kitchen — the carb-load base for Friday and Saturday dinners — plus washer and dryer, air-conditioning, a garden, parking on site and no lift. The Green Line’s Cottage Grove station (63rd & Cottage Grove) is about a 7-minute walk and reaches the Loop in ~25 minutes. The Midway Plaisance, two blocks north, is the spot for easy runs.',
        location: '6156 S St Lawrence Ave',
        time: 'Afternoon',
        image: '/images/chicago/loft.jpg',
      },
      {
        icon: <ShoppingBag size={24} />,
        title: 'Carb-load shop at Jewel-Osco',
        description:
          'The Woodlawn Jewel-Osco at 61st & Cottage Grove is about 5 minutes’ walk (6 am–10 pm). Buy everything on the Shopping tab except the GF bread and Fody oil, which come from Whole Foods Hyde Park tomorrow evening. Then an easy dinner in and bed on Chicago time.',
        location: '6014 S Cottage Grove Ave',
        time: 'Evening',
        image: '/images/chicago/green-line-cottage-grove.jpg',
      },
    ],
  },
  {
    date: 'Thursday, October 8',
    dayNumber: 2,
    title: 'The Bean, Art Institute & River Cruise',
    subtitle: 'T-3 · Carb-load ramp starts · one trip downtown, seated afternoon',
    theme: 'chicago-taper',
    activities: [
      {
        icon: <TrainFront size={24} />,
        title: 'Green Line to the Loop',
        description:
          'Breakfast at the loft at 7:00, then the Green Line from Cottage Grove at about 7:55 to Washington/Wabash (~25 min). Tap a contactless card or use the Ventra app — no ticket needed. Everything today is within a few blocks of the Wabash stops.',
        location: '63rd St & Cottage Grove Ave',
        time: '7:55 AM',
        image: '/images/chicago/green-line-cottage-grove.jpg',
      },
      {
        icon: <Coffee size={24} />,
        title: 'Intelligentsia Coffee Millennium Park',
        description:
          'Chicago’s own specialty roaster, founded here in 1995. The Randolph St coffeebar opens 6:30 am — get a pour-over of whatever single origin is on and take it across to the park. One block from Washington/Wabash, two minutes from Cloud Gate.',
        location: '53 E Randolph St',
        time: '8:30 AM',
        image: '/images/chicago/intelligentsia-millennium-park.jpg',
      },
      {
        icon: <Sparkles size={24} />,
        title: 'Cloud Gate (The Bean) at Millennium Park',
        description:
          'Anish Kapoor’s 110-ton mirrored sculpture, fully reopened since 2024. Early morning is the only time it’s not mobbed. Walk underneath into the “omphalos” for the warped reflections. Check the man in the bean is OK.',
        location: '201 E Randolph St',
        time: '9:15 AM',
        image: '/images/bean.jpg',
      },
      {
        icon: <Landmark size={24} />,
        title: 'Art Institute of Chicago — skip-the-line tour',
        description:
          'Meet at the bronze lions on Michigan Ave at 10:30 — the museum opens at 11:00 (open every day except Tuesday). The 2.5-hour guided tour covers the greatest Impressionist collection outside Paris, Seurat’s Sunday on La Grande Jatte, Hopper’s Nighthawks, Wood’s American Gothic, plus Picasso, Dalí and Warhol. It’s the longest stretch on your feet all week, so grab the gallery benches whenever the guide stops. Eat the 10:00 snack before you go in.',
        location: '111 S Michigan Ave',
        time: '10:30 AM',
        image: '/images/chicago/art-institute.jpg',
      },
      {
        icon: <Ship size={24} />,
        title: 'Chicago Architecture Center River Cruise',
        description:
          'Eat the packed ramp-day lunch sitting down in Millennium Park first. Then 90 minutes on the river, all of it seated. The CAC’s First Lady boats leave from the dock at the southeast corner of the Michigan Ave bridge. Docents cover the Wrigley Building, Tribune Tower, Marina City, the Merchandise Mart and Willis Tower across all three branches of the river. From $57; book a ~14:30 departure 1–2 weeks ahead (so book now).',
        location: '112 E Wacker Dr',
        time: '2:30 PM',
        image: '/images/chicago/first-lady-cruise.jpg',
      },
      {
        icon: <Utensils size={24} />,
        title: 'Quartino Ristorante — early dinner',
        description:
          'A 10-minute walk from the cruise dock, so you only make one trip downtown today. Italian small plates; it’s a ramp day, so don’t stress the grams: order plain pasta or risotto with olive oil and parmesan, sauce on the side, and say “no garlic, no onion” — US Italian sauces nearly always have both. Book a ~4:45 pm table on OpenTable, then Green Line home from State/Lake and feet up.',
        location: '626 N State St',
        time: '4:45 PM',
        image: '/images/chicago/quartino.jpg',
      },
    ],
  },
  {
    date: 'Friday, October 9',
    dayNumber: 3,
    title: 'Expo, Sushi Lunch & Bulls',
    subtitle: 'T-2 · Full load day · feet up between the Expo and tip-off',
    theme: 'chicago-taper',
    activities: [
      {
        icon: <Footprints size={24} />,
        title: 'Abbott Health & Fitness Expo — bib pickup',
        description:
          'An easy one from the loft: Green Line Cottage Grove → Cermak-McCormick Place is about 12 minutes, then a 10–15-minute walk to McCormick Place North, Level 3, Hall B1 (or a 10-minute car ride, dropping at the East Building). Open 9 am–6 pm; get there for opening. Bring government photo ID in your registration name and the pickup ticket — no proxy pickup, no race-day pickup. Grab the free poster on the right as you walk in and a free pace tattoo at the Pace Team booth. Cap it at 60–90 minutes and eat the 10:00 snack there.',
        location: '2301 S Martin Luther King Dr',
        time: '9:00 AM',
        image: '/images/chicago/perks/expo.jpg',
      },
      {
        icon: <Utensils size={24} />,
        title: 'Kai Sushi South Loop',
        description:
          'Lunch on the way home: one Green Line stop from Cermak-McCormick Place to Roosevelt, then a 3-minute walk (or a 10-minute cab from the Expo). It’s the 150 g lunch on the full-load day, and sushi rice is the easiest low-FODMAP carb on a menu. Order three simple maki rolls (salmon, tuna, cucumber) plus a side of plain rice, with soy sauce. Skip the tempura, spicy mayo and eel sauce. The restaurant’s site says it opens at 11 am on Fridays, but Google lists 4 pm, so ring ahead on 312-583-0567. Afterwards it’s the Green Line from Roosevelt back to Cottage Grove.',
        location: '1255 S State St',
        time: '12:30 PM',
        image: '/images/chicago/kai-sushi-south-loop.jpg',
      },
      {
        icon: <Hotel size={24} />,
        title: 'Feet up & early load dinner at the loft',
        description:
          'Back to the loft for the afternoon — legs up, 15:00 snack, then dinner at 16:45: GF pasta, plain passata, chicken and toast with maple. Eating early clears the Bulls game, and cooking it yourself is the only way to guarantee no garlic or onion.',
        location: '6156 S St Lawrence Ave',
        time: '3:00 PM',
        image: '/images/chicago/loft.jpg',
        muted: true,
      },
      {
        icon: <Trophy size={24} />,
        title: 'Chicago Bulls v Memphis Grizzlies',
        description:
          'Booked. Preseason NBA at the United Center, tip-off 7:00 pm — seated, which suits two days out from the race. About 25–30 minutes by car from Woodlawn; leave by 6:00 for security. Michael Jordan’s statue is in the atrium. Take the evening load snack (rice bars, banana) if the arena allows outside food — check the United Center bag policy first — or eat it before you leave. Car home afterwards; back by ~10:30 pm.',
        location: '1901 W Madison St',
        time: '7:00 PM',
        image: '/images/chicago/united-center.jpg',
      },
    ],
  },
  {
    date: 'Saturday, October 10',
    dayNumber: 4,
    title: 'Chicago 5K, lululemon Shakeout & Taper',
    subtitle: 'T-1 · Peak load · back at the loft by late morning · in bed by 20:30',
    theme: 'chicago-taper',
    activities: [
      {
        icon: <Users size={24} />,
        title: 'Spectate the Abbott Chicago 5K',
        description:
          'Breakfast at the loft at 6:15, then the Green Line from Cottage Grove at about 6:50 to Adams/Wabash (~25 min). The 5K starts at 7:30 in Grant Park and loops 3.1 miles past downtown landmarks; the finish closes at 9:00. Watch the start and the first finishers, then head north by 8:10 — and resist the urge to walk the whole course. It’s sold out, so it’s spectating only.',
        location: 'Grant Park',
        time: '7:30 AM',
        image: '/images/chicago/buckingham-fountain.jpg',
      },
      {
        icon: <Footprints size={24} />,
        title: 'lululemon shakeout',
        description:
          'An easy 8:30 shakeout run from lululemon in Streeterville (60611). In 2025 it was a 5K from the Rush St store with refreshments and light bites. The exact address and RSVP are on the marathon-weekend.com listing — check it on Friday. It’s about 10 minutes by car from Grant Park. Keep the run conversational, carry the 10:00 snack and a Gatorade, then take a car back to the loft by about 10:30.',
        time: '8:30 AM',
        image: '/images/chicago/mag-mile.jpg',
      },
      {
        icon: <Hotel size={24} />,
        title: 'Rest & minimal movement',
        description:
          'Back to the loft by late morning. Legs up, kit laid out, gels counted (8, caf ones marked), race-morning food staged, PF Carb Mix pre-measured, and the car booked for 5:30 am. The bib is already collected, so there’s nowhere you need to be. Every meal today is at the loft — see the Fuelling tab for the 810 g plan.',
        location: '6156 S St Lawrence Ave',
        time: 'Afternoon',
        image: '/images/chicago/loft.jpg',
        muted: true,
      },
      {
        icon: <Utensils size={24} />,
        title: 'Final load meal at the loft',
        description:
          'Rice, tuna, salt and GF toast with jam at 4:30 pm, then rice bars, a banana and the final Nomio shot at 6:30 pm. Last bite at 6:45 pm. Shifted about 90 minutes earlier than London so a 3:45 am alarm still gives seven hours in bed.',
        time: '4:30 PM',
        image: '/images/chicago/loft.jpg',
        muted: true,
      },
    ],
  },
  {
    date: 'Sunday, October 11',
    dayNumber: 5,
    title: 'RACE DAY',
    subtitle: 'Bank of America Chicago Marathon · Wave 1 07:35',
    theme: 'chicago-race',
    highlight: true,
    activities: [
      {
        icon: <Footprints size={24} />,
        title: 'Bank of America Chicago Marathon',
        description:
          '26.2 miles through 29 neighbourhoods, starting on Columbus Dr at Monroe. Getting there (Wave 1): a pre-booked car leaves the loft at 5:30 for the official rideshare drop-off, west of Michigan Ave and south of Ida B. Wells Dr — about 25 minutes, before the 6 am road closures bite. Then walk to the gate on your bib (Jackson, Van Buren, Ida B. Wells or Harrison). Backup: the Green Line from Cottage Grove to Roosevelt (~18 min) and walk north — check the first Sunday train on Saturday night. Corrals close at 7:20. You can’t pop back to the loft, so use gear check (clear race bag) for ID, phone and a dry top — you need the ID for the free beer. Miles 1–2 reportedly run down Dearborn instead of State this year. Gatorade comes before water at all 20 aid stations, Maurten gels are handed out at miles 12.4 and 18.1, and the only climb is the Roosevelt Rd bridge about 500 m from the line.',
        location: 'Grant Park',
        time: '7:35 AM',
        image: '/images/chicago/marathon-start-grant-park.jpg',
      },
      {
        icon: <Beer size={24} />,
        title: '27th Mile Post-Race Party',
        description:
          'Butler Field, right out of the finish chute (9:30 am–4 pm). Trade the bib’s tear-off tab for a free beer — Goose Island 312, Goose IPA or Michelob ULTRA (photo ID, 21+). Supporters enter via Gate 1 (Jackson) or Gate 4 (Ida B. Wells). The reunite towers are here, so agree on a letter with anyone tracking you on the race app.',
        location: 'Butler Field, Grant Park',
        time: 'Late Morning',
        image: '/images/chicago/butler-field-party.jpg',
      },
      {
        icon: <Sparkles size={24} />,
        title: 'Tracksmith after-party & finish-time poster',
        description:
          'From 12:00 at the Chicago Athletic Association on Michigan Ave, a short walk from Butler Field. They stamp a free poster with your finish time (12:00–17:00, limited stock), plus a drink. Hoodie chain-stitching is paid.',
        location: '12 S Michigan Ave',
        time: '12:00 PM',
        image: '/images/chicago/chicago-athletic-association.jpg',
      },
      {
        icon: <Utensils size={24} />,
        title: "Lou Malnati's South Loop — post-race deep dish",
        description:
          'Post-race deep dish: the South Loop branch at 805 S State (11 am–11 pm) is right by Roosevelt station — on your way home. Order the Malnati Chicago Classic, with sausage, extra cheese and tomatoes on the Buttercrust. It takes 30–40 minutes to bake, so order on arrival or call ahead, and expect a marathon-day queue. Then the Green Line from Roosevelt back to Cottage Grove.',
        location: '805 S State St',
        time: '2:00 PM',
        image: '/images/chicago/lou-malnatis-south-loop.jpg',
      },
      {
        icon: <Hotel size={24} />,
        title: 'Legs up at the loft',
        description:
          'The evening is yours: shower, legs up the wall, fluids with electrolytes, an early night. No sauna today — you’re dehydrated.',
        location: '6156 S St Lawrence Ave',
        time: 'Evening',
        image: '/images/chicago/loft.jpg',
        muted: true,
      },
    ],
  },
  {
    date: 'Monday, October 12',
    dayNumber: 6,
    title: 'Medal Monday, AIRE & Skydeck',
    subtitle: 'Last full day · Columbus Day · early dinner, early night',
    theme: 'chicago-recovery',
    activities: [
      {
        icon: <Medal size={24} />,
        title: 'Free medal engraving',
        description:
          'Medal Monday: lululemon and PUMA’s NITRO Lab (both Streeterville / Mag Mile) engrave medals from 10:00 — the free alternative to the paid itab plate. Lines have run to about an hour, so be there at opening (car from the loft ~25 min, or the Green Line to State/Lake). Exact addresses and RSVP terms are on the marathon-weekend.com listing; check on Sunday.',
        time: '10:00 AM',
        image: '/images/chicago/mag-mile.jpg',
      },
      {
        icon: <Coffee size={24} />,
        title: 'Metric Coffee',
        description:
          'Xavier Alexander and Darko Marjanovic’s roastery-café on Fulton, open from 7 am. The café shares the space with the roaster, so ask what came off the Probat that morning. A short ride west after engraving, and well away from the Columbus Day Parade, which closes State St south of the river at noon.',
        location: '2021 W Fulton St',
        time: '11:15 AM',
        image: '/images/chicago/metric-coffee.jpg',
      },
      {
        icon: <Waves size={24} />,
        title: 'AIRE Ancient Baths Chicago',
        description:
          'Candlelit thermal baths in a converted industrial building in River West: tepidarium, caldarium, frigidarium, a salt-water flotarium and a steam room. Usually 8 am–10 pm, but holiday hours may vary today, so confirm when you book (chicago@beaire.com). Add the 30-minute massage. Sessions are capacity-limited — book ahead.',
        location: '800 W Superior St',
        time: '12:30 PM',
        image: '/images/chicago/aire.jpg',
      },
      {
        icon: <Sparkles size={24} />,
        title: 'Willis Tower Skydeck & The Ledge',
        description:
          'Glass boxes 1,353 ft up on the 103rd floor, with views across four states on a clear day. 9 am–8 pm, timed tickets from $32 — book a slot so you’re not queueing on marathon legs. Kindling, at the base of the tower, gives 20% off with your medal today if you want a late lunch.',
        location: '233 S Wacker Drive',
        time: '3:15 PM',
        image: '/images/chicago/skydeck-ledge.jpg',
      },
      {
        icon: <Building2 size={24} />,
        title: 'Medieval Torture Museum (optional)',
        description:
          'If the legs allow: 6,000 sq ft of torture, interrogation and execution devices, with wax figures and an audio guide, about $34. The “ghost hunting experience” is an app-based hunt included with every ticket, so charge your phone. 15 minutes from the Skydeck; allow an hour.',
        location: '177 N State St',
        time: '4:30 PM',
        image: '/images/chicago/torture-museum.jpg',
      },
      {
        icon: <Utensils size={24} />,
        title: 'Girl & The Goat',
        description:
          'Last dinner, booked early because of the 6 am flight. Stephanie Izard’s West Loop original opens at 4:30 pm. Order the wood-fired goat empanadas, the roasted pig face, and the sautéed green beans with fish-sauce vinaigrette. Book on OpenTable, then a car back to the loft (~25 min), pack and get to bed.',
        location: '809 W Randolph St',
        time: '6:00 PM',
        image: '/images/chicago/girl-and-the-goat.jpg',
      },
    ],
  },
  {
    date: 'Tuesday, October 13',
    dayNumber: 7,
    title: 'Departure',
    subtitle: 'AA607 · 06:00 from O’Hare',
    theme: 'chicago-arrival',
    activities: [
      {
        icon: <Plane size={24} />,
        title: "Loft → O'Hare",
        description:
          'AA607 leaves at 6:00 am. Leave the loft by about 3:30 am — Woodlawn to O’Hare is 35–45 minutes at that hour, and you’ll want time to check bags through to Melbourne. Pre-book the car on Monday; the trains are slow overnight.',
        location: "O'Hare International Airport",
        time: '3:30 AM',
        image: '/images/chicago/ohare.jpg',
      },
    ],
  },
];

export const chicagoBookingChecklist: NoteItem[] = [
  { label: 'Airport car (arrival)', text: 'Wed 7, O’Hare → 6156 S St Lawrence Ave, after AA2998 lands.' },
  { label: 'Art Institute tour', text: 'Thu 8, meet 10:30 at the lions (museum opens 11:00). Move the booking if it was for Wed 7.' },
  {
    label: 'Architecture river cruise',
    text: 'Thu 8, ~14:30, Chicago Architecture Center (First Lady). Books out 1–2 weeks ahead, so book today.',
  },
  { label: 'Quartino', text: 'Thu 8, ~4:45 pm on OpenTable. Order plain, no garlic or onion.' },
  { label: 'Expo', text: 'Fri 9 from 9:00. Photo ID + pickup ticket; no proxy, no race-day pickup.' },
  { label: 'Kai Sushi South Loop', text: 'Fri 9, ~12:30 lunch. Ring 312-583-0567 to confirm it opens at 11 am (Google says 4 pm).' },
  { label: 'Bulls v Grizzlies', text: 'Fri 9, 7:00 pm, United Center. Booked — check the bag policy; car there and back.' },
  { label: 'lululemon shakeout', text: 'Sat 10, 8:30, Streeterville — RSVP and exact address on the marathon-weekend.com listing.' },
  { label: 'Race-morning car', text: 'Sun 11, 5:30 am pickup → rideshare drop-off west of Michigan, south of Ida B. Wells.' },
  { label: "Lou Malnati's South Loop", text: 'Sun 11, ~2 pm. Call ahead or join the waitlist; deep dish takes 30–40 min.' },
  {
    label: 'AIRE Ancient Baths',
    text: 'Mon 12, ~12:30 session. Capacity-limited and holiday hours may vary: chicago@beaire.com · +1 312-312-9610.',
  },
  { label: 'Skydeck', text: 'Mon 12, ~15:15 timed ticket online.' },
  { label: 'Girl & The Goat', text: 'Mon 12, 6:00 pm on OpenTable.' },
  { label: 'Airport car (departure)', text: 'Tue 13, pickup 3:30 am for AA607 at 6:00 am.' },
];

export const chicagoQuickReference: NoteItem[] = [
  {
    label: 'Race morning',
    text: 'Wave 1 07:35 (corrals close 07:20) · Wave 2 08:00 · Wave 3 08:35. Grant Park opens 05:30. Car from the loft at 5:30 to the rideshare drop-off (west of Michigan, south of Ida B. Wells); Green Line to Roosevelt as backup. Use gear check.',
  },
  {
    label: 'Getting around',
    text: 'Green Line from Cottage Grove (63rd & Cottage, ~7 min walk): Cermak-McCormick Place ~12 min, Roosevelt ~18 min, Adams/Wabash and Washington/Wabash ~25 min. Cars for O’Hare, the United Center and anything after dark.',
  },
  {
    label: 'Coffee',
    text: 'Intelligentsia (Randolph, Thu) and Metric (Fulton, Mon). Coffee at the loft on the Expo and race-eve mornings.',
  },
  {
    label: 'Restaurants',
    text: "Quartino (Thu, early ramp-day dinner, order plain), Lou Malnati's South Loop (race day, on the way home), Girl & The Goat (Mon, early).",
  },
  {
    label: 'Carb-load groceries',
    text: 'Jewel-Osco, 6014 S Cottage Grove (6 am–10 pm, ~5 min walk); Whole Foods Hyde Park, 5118 S Lake Park Ave (8 am–10 pm) for Canyon Country White bread and Fody oil. Buy race breakfast by Saturday.',
  },
  {
    label: 'Finisher perks',
    text: 'Free beer at the 27th Mile party, a stamped Tracksmith poster, free medal engraving on Monday, and free drinks at Toro, Michael Jordan’s, The Gwen and Portofino. The Race Week tab has the full list.',
  },
  {
    label: 'Supporters',
    text: 'From the loft, the Green Line reaches mile 23 (35th-Bronzeville-IIT) and mile 25 (Cermak-McCormick Place) directly. Best no-crossing downtown route: Chicago & LaSalle (3.5) → North & Sedgwick (11) → Chinatown (21.5) → Michigan & Cermak (25). Race Week tab has passing times.',
  },
  {
    label: 'Holidays & closures',
    text: 'Mon 12 is Columbus Day: the parade is on State St downtown at noon. Museums, AIRE and restaurants are open.',
  },
  {
    label: 'Cut for the dates and the move to Woodlawn',
    text: 'White Sox playoff game (Wed 3 pm, clashes with landing), Purple Pig, Field Museum, Shedd, Sawada, Navy Pier, Indiana Dunes, Gibsons, Barry’s, La Colombe and RPM Italian. All still verified open if plans change.',
  },
];
